import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../lib/api';
import { Users, UserCheck, UserX, Shield, Edit2, Power, X, Check, Clock, Plus, KeyRound, Copy, Upload, RefreshCw, UserPlus, Info, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import BulkEmployeeImportModal from '../components/BulkEmployeeImportModal';
import DepartmentCombobox from '../components/DepartmentCombobox';
import { useConfirm } from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const FALLBACK_DEPARTMENTS = [
  "Business Development", "Tendering", "Engineering", "Procurement",
  "Finance & Accounts", "HR & Admin", "Projects/Site",
  "Quality", "Safety", "Contracts & Legal", "IT", "MD"
];

export default function UserManagement() {
  const { user: currentUser } = useAuth() || {};
  const canAddUsers = (() => {
    const role = (currentUser?.role || '').toLowerCase();
    const dept = (currentUser?.department || '').toUpperCase();
    // Super Admin always, plus IT department (per company policy: IT manages all logins)
    return role === 'super_admin' || dept === 'IT';
  })();
  const [confirm, ConfirmHost] = useConfirm();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [stats, setStats] = useState(null);
  // single source of truth from /api/user-management/departments
  const [DEPARTMENTS, setDEPARTMENTS] = useState(FALLBACK_DEPARTMENTS);
  // Iter 70 Phase 2 — RBAC roles list for the role-assignment dropdown
  const [rbacRoles, setRbacRoles] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, approved
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    department: '',
    role: 'user',
    can_approve_users: false
  });

  // New user creation
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', name: '', phone: '', employee_id: '', department: '', role: 'user', role_id: '', can_approve_users: false });
  // Edit user
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ user_id: '', name: '', email: '', phone: '', employee_id: '', department: '', role: 'user', role_id: '', role_id_initial: '', can_approve_users: false, is_active: true, approval_status: 'approved' });
  // Password modal (used both for create result + reset result)
  const [passwordModal, setPasswordModal] = useState(null); // { email, password, message }
  // Bulk import
  const [showBulkImport, setShowBulkImport] = useState(false);

  const loadData = async () => {
    try {
      const [usersRes, pendingRes, statsRes, rolesRes, deptRes] = await Promise.all([
        apiGet('/user-management/users'),
        apiGet('/user-management/pending'),
        apiGet('/user-management/stats'),
        apiGet('/rbac/roles').catch(() => ({ rows: [] })),
        apiGet('/user-management/departments').catch(() => ({ departments: FALLBACK_DEPARTMENTS })),
      ]);
      setUsers(usersRes.users || []);
      setPendingUsers(pendingRes.pending_users || []);
      if (deptRes?.departments?.length) setDEPARTMENTS(deptRes.departments);
      setStats(statsRes);
      setRbacRoles(rolesRes.rows || []);
    } catch (err) {
      console.error('Failed to load user management data:', err);
      toast.error('Failed to load users');
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleApprove = (user) => {
    setSelectedUser(user);
    setApprovalForm({ department: '', role: 'user', can_approve_users: false });
    setShowApprovalModal(true);
  };

  const submitApproval = async (e) => {
    e.preventDefault();
    if (!approvalForm.department) {
      toast.error('Please select a department');
      return;
    }
    try {
      await apiPost(`/user-management/users/${selectedUser.user_id}/approve`, {
        action: 'approve',
        ...approvalForm
      });
      toast.success('User approved successfully');
      setShowApprovalModal(false);
      loadData();
    } catch (err) {
      toast.error(err.detail || 'Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user?')) return;
    try {
      await apiPost(`/user-management/users/${userId}/approve`, { action: 'reject' });
      toast.success('User rejected');
      loadData();
    } catch (err) {
      toast.error('Failed to reject user');
    }
  };

  const handleToggleActive = async (u) => {
    const newActive = !u.is_active;
    const ok = await confirm({
      title: newActive ? `Enable ${u.name}?` : `Disable ${u.name}?`,
      body: newActive
        ? `Re-enabling ${u.email} will restore their login access immediately.`
        : `Disabling ${u.email} will block all logins for this user.\n\nAll their historical data (attendance, audit logs, created records) stays intact. You can re-enable them anytime.\n\nUser accounts are never permanently deleted to preserve audit history.`,
      confirmLabel: newActive ? 'Enable User' : 'Disable User',
      danger: !newActive,
    });
    if (!ok) return;
    try {
      await apiPut(`/user-management/users/${u.user_id}/toggle-active`, { is_active: newActive });
      toast.success(newActive ? 'User enabled' : 'User disabled');
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update user status');
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.name || !createForm.department) {
      toast.error('Name, email, department are required');
      return;
    }
    try {
      const { role_id, ...createPayload } = createForm;
      const res = await apiPost('/user-management/users', createPayload);
      // apply RBAC role_id post-create so the user picks up the
      // matrix immediately (rather than the legacy department default).
      if (role_id) {
        try { await apiPut(`/rbac/users/${res.user.user_id}/role`, { role_id }); }
        catch (e2) { /* keep going even if role assign fails — show toast */
          toast.error('User created but role assignment failed: ' + (e2?.response?.data?.detail || e2.message));
        }
      }
      toast.success(`User ${res.user.email} created`);
      setShowCreate(false);
      setCreateForm({ email: '', name: '', phone: '', employee_id: '', department: '', role: 'user', role_id: '', can_approve_users: false });
      setPasswordModal({ email: res.user.email, password: res.initial_password, message: res.message });
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create user');
    }
  };

  const resetUserPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? They'll be forced to change it on next login.`)) return;
    try {
      const res = await apiPost(`/user-management/users/${u.user_id}/reset-password`, {});
      setPasswordModal({ email: u.email, password: res.new_password, message: res.message });
      toast.success('Password reset — share securely with the user');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    }
  };

  const openEdit = (u) => {
    setEditForm({
      user_id: u.user_id,
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      employee_id: u.employee_id || '',
      department: u.department || '',
      role: u.role || 'user',
      role_id: u.role_id || '',
      role_id_initial: u.role_id || '',
      can_approve_users: !!u.can_approve_users,
      is_active: u.is_active !== false,
      approval_status: u.approval_status || 'approved',
      is_super_admin: u.role === 'super_admin',
    });
    setShowEdit(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    try {
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase(),
        phone: editForm.phone,
        employee_id: editForm.employee_id || null,
        department: editForm.department || null,
        role: editForm.role,
        can_approve_users: editForm.can_approve_users,
        is_active: editForm.is_active,
        approval_status: editForm.approval_status,
      };
      await apiPut(`/user-management/users/${editForm.user_id}`, payload);
      // Iter 70 Phase 2 — if RBAC role_id changed, persist via dedicated endpoint
      if (editForm.role_id && editForm.role_id !== editForm.role_id_initial) {
        try {
          await apiPut(`/rbac/users/${editForm.user_id}/role`, { role_id: editForm.role_id });
        } catch (e) {
          toast.error(`User saved but role assignment failed: ${e?.response?.data?.detail || ''}`);
        }
      }
      toast.success('User updated');
      setShowEdit(false);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const syncFromEmployee = async (u) => {
    if (!window.confirm(`Sync ${u.name}'s profile from Employee Master? Existing name / email / phone / department / employee_id will be overwritten with the latest employee record.`)) return;
    try {
      const res = await apiPost(`/user-management/users/${u.user_id}/sync-employee`, {});
      const applied = res.applied || 0;
      const skipped = res.skipped || 0;
      const changeKeys = Object.keys(res.changes || {});
      if (applied === 0 && skipped === 0) {
        toast.info(`${u.name} is already in sync with Employee Master.`);
      } else {
        const skipMsg = skipped ? ` · ${skipped} skipped` : '';
        toast.success(`Synced ${applied} field${applied === 1 ? '' : 's'}: ${changeKeys.join(', ')}${skipMsg}`);
      }
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    }
  };

  const copyToClipboard = (txt) => {
    navigator.clipboard.writeText(txt).then(() => toast.success('Copied to clipboard'));
  };

  const filteredUsers = filter === 'pending' 
    ? pendingUsers 
    : filter === 'approved' 
      ? users.filter(u => u.approval_status === 'approved')
      : users;

  const statusColor = (status) => {
    if (status === 'approved') return 'bg-emerald-100 text-emerald-700';
    if (status === 'pending') return 'bg-amber-100 text-amber-700';
    if (status === 'rejected') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  const statusIcon = (status) => {
    if (status === 'approved') return <UserCheck size={14} />;
    if (status === 'pending') return <Clock size={14} />;
    if (status === 'rejected') return <UserX size={14} />;
    return null;
  };

  return (
    <div>
      {ConfirmHost}
      <Link to="/app/settings" className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 mb-3" data-testid="back-to-settings-btn">
        <ArrowLeft size={12} /> Back to Settings
      </Link>
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Administration</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">User Management</h1>
          <p className="text-sm text-slate-600 mt-1">Manage user access, departments, and approvals</p>
        </div>
        <div className="flex items-center gap-2">
          {canAddUsers && (
            <button
              onClick={() => { setCreateForm({ email: '', name: '', phone: '', employee_id: '', department: '', role: 'user', can_approve_users: false }); setShowCreate(true); }}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-sm font-semibold"
              data-testid="add-non-employee-user-btn"
              title="Create a login for an external collaborator (consultant, vendor, auditor) without an employee record"
            >
              <UserPlus size={16} /> Add User
            </button>
          )}
          <button
            onClick={() => setShowBulkImport(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-sm hover:border-[#1F7A6E] hover:text-[#1F7A6E] text-sm font-semibold"
            data-testid="bulk-import-open-btn"
          >
            <Upload size={16} /> Bulk Import
          </button>
        </div>
      </div>

      {/* Helper note — guide on which path to use */}
      <div className="bg-teal-50/60 border border-teal-200 rounded-sm px-4 py-3 mb-6 text-sm text-slate-700" data-testid="user-creation-note">
        <b>Internal employees:</b> Add via <span className="font-display font-bold text-[#1F7A6E]">HR → Employee Master → Add Employee</span> — creates the employee record AND a login in one step.
        <br />
        <b>External users</b> (consultants, vendors, auditors, clients): Use <span className="font-display font-bold text-[#1F7A6E]">Add User</span> above — creates a login only, with no payroll/HR linkage.
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <Users size={18} className="text-[#1F7A6E]" />
              <span className="text-xs font-display uppercase text-slate-500">Total Users</span>
            </div>
            <div className="font-display text-2xl font-bold">{stats.total_users}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <UserCheck size={18} className="text-emerald-600" />
              <span className="text-xs font-display uppercase text-slate-500">Approved</span>
            </div>
            <div className="font-display text-2xl font-bold text-emerald-700">{stats.approved}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} className="text-amber-600" />
              <span className="text-xs font-display uppercase text-slate-500">Pending</span>
            </div>
            <div className="font-display text-2xl font-bold text-amber-700">{stats.pending}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <UserX size={18} className="text-red-600" />
              <span className="text-xs font-display uppercase text-slate-500">Rejected</span>
            </div>
            <div className="font-display text-2xl font-bold text-red-700">{stats.rejected}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex gap-3">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
            filter === 'all' ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'border-slate-300 hover:border-[#1F7A6E]'
          }`}
        >
          All Users
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
            filter === 'pending' ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'border-slate-300 hover:border-[#1F7A6E]'
          }`}
        >
          Pending Approval ({pendingUsers.length})
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
            filter === 'approved' ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'border-slate-300 hover:border-[#1F7A6E]'
          }`}
        >
          Approved
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Department</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.user_id}>
                <td>
                  <div className="flex items-center gap-2">
                    {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
                    <div>
                      <div className="font-display font-semibold text-sm">{user.name}</div>
                      {user.role === 'super_admin' && (
                        <span className="text-xs text-purple-700 flex items-center gap-1">
                          <Shield size={10} /> Super Admin
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="text-xs">
                  {user.email}
                  {user.email.endsWith('@elloraepc.com') && (
                    <span className="ml-2 tag bg-teal-100 text-teal-700">Ellora</span>
                  )}
                </td>
                <td className="text-xs">{user.department || '-'}</td>
                <td className="text-xs capitalize">{user.role || 'user'}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <span className={`tag ${statusColor(user.approval_status)} flex items-center gap-1 w-fit`}>
                      {statusIcon(user.approval_status)}
                      {user.approval_status}
                    </span>
                    {user.is_active === false && (
                      <span className="tag bg-rose-100 text-rose-700 text-[10px] uppercase tracking-wider font-bold" data-testid={`disabled-badge-${user.user_id}`}>
                        Disabled
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-xs">{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="flex gap-2">
                    {user.approval_status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(user)}
                          className="text-emerald-600 hover:text-emerald-700"
                          title="Approve"
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={() => handleReject(user.user_id)}
                          className="text-red-600 hover:text-red-700"
                          title="Reject"
                        >
                          <X size={16} />
                        </button>
                      </>
                    )}
                    {user.approval_status === 'approved' && (
                      <>
                        <button
                          onClick={() => openEdit(user)}
                          className="text-[#1F7A6E] hover:text-[#2f3a5d]"
                          title="Edit user"
                          data-testid={`edit-user-${user.user_id}`}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => syncFromEmployee(user)}
                          className="text-emerald-600 hover:text-emerald-700"
                          title="Sync from Employee Master"
                          data-testid={`sync-emp-${user.user_id}`}
                        >
                          <RefreshCw size={14} />
                        </button>
                        <button
                          onClick={() => resetUserPassword(user)}
                          className="text-[#1F7A6E] hover:text-[#2f3a5d]"
                          title="Reset password"
                          data-testid={`reset-pw-${user.user_id}`}
                        >
                          <KeyRound size={14} />
                        </button>
                      </>
                    )}
                    {user.role !== 'super_admin' && (
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={user.is_active === false ? 'text-emerald-600 hover:text-emerald-700' : 'text-rose-600 hover:text-rose-700'}
                        title={user.is_active === false ? 'Enable user' : 'Disable user'}
                        data-testid={`toggle-active-${user.user_id}`}
                      >
                        <Power size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">No users found</div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full">
            <div className="bg-white border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Approve User</h2>
              <button onClick={() => setShowApprovalModal(false)} className="text-slate-500 hover:text-slate-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={submitApproval} className="p-6 space-y-4">
              <div>
                <div className="text-sm mb-4">
                  <strong>{selectedUser.name}</strong>
                  <div className="text-slate-600">{selectedUser.email}</div>
                </div>
              </div>
              <div>
                <label className="label">Department *</label>
                <select
                  value={approvalForm.department}
                  onChange={(e) => setApprovalForm({...approvalForm, department: e.target.value})}
                  className="input"
                  required
                >
                  <option value="">Select Department</option>
                  {DEPARTMENTS.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Role</label>
                <select
                  value={approvalForm.role}
                  onChange={(e) => setApprovalForm({...approvalForm, role: e.target.value})}
                  className="input"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="can_approve"
                  checked={approvalForm.can_approve_users}
                  onChange={(e) => setApprovalForm({...approvalForm, can_approve_users: e.target.checked})}
                />
                <label htmlFor="can_approve" className="text-sm">Can approve other users</label>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button type="button" onClick={() => setShowApprovalModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Approve User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CREATE USER MODAL */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-lg w-full rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={submitCreate}>
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="font-display text-xl font-bold">Add Non-Employee User</h2>
                  <p className="text-xs text-slate-500 mt-0.5">For consultants, vendors, or external collaborators who need a login but aren't on the payroll. Internal staff should be added via <a href="/app/hr/employees" className="text-[#1F7A6E] underline font-bold">Employee Master</a> — that creates the employee + login in one step.</p>
                </div>
                <button type="button" onClick={() => setShowCreate(false)} className="p-2 hover:bg-slate-100 rounded"><X size={18} /></button>
              </div>
              <div className="p-6 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Full Name *</label>
                    <input type="text" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" required data-testid="create-user-name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Work Email *</label>
                    <input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" required data-testid="create-user-email" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Phone</label>
                    <input type="text" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Employee ID</label>
                    <input type="text" value={createForm.employee_id} onChange={(e) => setCreateForm({ ...createForm, employee_id: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Department *</label>
                    <div className="mt-1">
                      <DepartmentCombobox
                        value={createForm.department}
                        onChange={(d) => setCreateForm({ ...createForm, department: d })}
                        required
                        testid="create-user-dept"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Role</label>
                    <select value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm">
                      <option value="user">User</option>
                      <option value="HR">HR</option>
                      <option value="PM">PM</option>
                      <option value="Director">Director</option>
                      <option value="MD">MD</option>
                    </select>
                  </div>
                </div>
                {/* Explicit Access-Control Role (decoupled from Department) */}
                <div className="bg-teal-50/40 border border-teal-200 rounded-sm p-3" data-testid="create-user-rbac">
                  <label className="text-xs uppercase font-display font-bold tracking-widest text-[#1F7A6E] block mb-1">
                    Access-Control Role <span className="text-slate-400 font-normal normal-case tracking-normal">(determines module × action permissions)</span>
                  </label>
                  <select
                    value={createForm.role_id || ''}
                    onChange={(e) => setCreateForm({ ...createForm, role_id: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                    data-testid="create-user-role-id"
                  >
                    <option value="">— No specific role (user will have NO module access until assigned) —</option>
                    {rbacRoles.filter(r => r.id !== 'role_super_admin').map(r => (
                      <option key={r.id} value={r.id}>
                        {r.name}{r.grade ? ` · ${r.grade}` : ''}{r.is_system ? ' [System]' : ' [Custom]'}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                    Edit role permissions in <a href="/app/settings/roles" target="_blank" rel="noreferrer" className="text-[#1F7A6E] font-bold underline">Settings → Roles &amp; Access Control</a>.
                  </p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={createForm.can_approve_users} onChange={(e) => setCreateForm({ ...createForm, can_approve_users: e.target.checked })} />
                  Can approve other users
                </label>
              </div>
              <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-slate-300 rounded text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#1F7A6E] text-white rounded text-sm font-semibold" data-testid="submit-create-user">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-lg w-full rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={submitEdit}>
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="font-display text-xl font-bold">Edit User</h2>
                  <p className="text-xs text-slate-500">Changing the email will rename the login.</p>
                </div>
                <button type="button" onClick={() => setShowEdit(false)} className="p-2 hover:bg-slate-100 rounded"><X size={18} /></button>
              </div>
              <div className="p-6 space-y-3">
                {editForm.is_super_admin && (
                  <div className="text-xs bg-purple-50 border border-purple-200 text-purple-800 px-3 py-2 rounded flex items-center gap-2">
                    <Shield size={12} /> This is a Super Admin account. Role cannot be downgraded here.
                  </div>
                )}
                <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-[11px] text-slate-700 flex items-start gap-2" data-testid="edit-user-clarifier">
                  <Info size={14} className="mt-0.5 flex-shrink-0 text-[#1F7A6E]" />
                  <div className="leading-relaxed">
                    <strong>Department</strong> is where this person sits in the org (used for reporting, team filters, and inbox routing).
                    <strong className="ml-1">Access-Control Role</strong> (below) decides what they can <em>see and do</em> across modules.
                    Changing the department <strong>will not</strong> change their permissions — assign the role explicitly.
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Full Name *</label>
                    <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" required data-testid="edit-user-name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Email *</label>
                    <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" required data-testid="edit-user-email" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Phone</label>
                    <input type="text" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Employee ID</label>
                    <input type="text" value={editForm.employee_id} onChange={(e) => setEditForm({ ...editForm, employee_id: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Department</label>
                    <div className="mt-1">
                      <DepartmentCombobox
                        value={editForm.department}
                        onChange={(d) => setEditForm({ ...editForm, department: d })}
                        testid="edit-user-dept"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-600">Approval Status</label>
                    <select value={editForm.approval_status} onChange={(e) => setEditForm({ ...editForm, approval_status: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm bg-white">
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />
                      Account is active
                    </label>
                  </div>
                </div>

                {/* Iter 70 Phase 2 — RBAC role assignment */}
                <div className="bg-teal-50/40 border border-teal-200 rounded-sm p-3" data-testid="edit-user-rbac">
                  <label className="text-xs uppercase font-display font-bold tracking-widest text-[#1F7A6E] block mb-1">
                    Access-Control Role <span className="text-slate-400 font-normal normal-case tracking-normal">(determines module × action permissions)</span>
                  </label>
                  <select
                    value={editForm.role_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, role_id: e.target.value })}
                    disabled={editForm.is_super_admin}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm bg-white disabled:bg-slate-100"
                    data-testid="edit-user-role-id"
                  >
                    <option value="">— No specific role (use legacy department defaults) —</option>
                    {rbacRoles.filter(r => r.id !== 'role_super_admin').map(r => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                        {r.grade ? ` · ${r.grade}` : ''}
                        {r.is_system ? ' [System]' : ' [Custom]'}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                    Edit role permissions in <a href="/app/settings/roles" target="_blank" rel="noreferrer" className="text-[#1F7A6E] font-bold underline">Settings → Roles &amp; Access Control</a>.
                    Super Admin role cannot be assigned or removed from here.
                  </p>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editForm.can_approve_users} onChange={(e) => setEditForm({ ...editForm, can_approve_users: e.target.checked })} />
                  Can approve other users
                </label>
              </div>
              <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                <button type="button" onClick={() => setShowEdit(false)} className="px-4 py-2 border border-slate-300 rounded text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#1F7A6E] text-white rounded text-sm font-semibold" data-testid="submit-edit-user">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GENERATED-PASSWORD MODAL */}
      {passwordModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
              <KeyRound className="text-amber-600" size={22} />
              <div>
                <h2 className="font-display text-lg font-bold">Password Generated</h2>
                <p className="text-xs text-slate-500">{passwordModal.email}</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                ⚠️ Copy this password now — it cannot be retrieved again. Share via a secure channel.
              </div>
              {/* Password display — large, unambiguous font */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-semibold text-slate-600">Temporary Password</span>
                  <button onClick={() => { copyToClipboard(passwordModal.password); }}
                    className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-[#1F7A6E] text-white rounded text-xs font-bold hover:bg-[#14504E]"
                    data-testid="copy-password-btn">
                    <Copy size={12} /> Copy
                  </button>
                </div>
                <code className="block w-full px-4 py-4 bg-slate-900 text-emerald-400 border border-slate-700 rounded font-mono text-2xl tracking-[0.35em] select-all text-center letter-spacing-wide"
                  data-testid="generated-password">
                  {passwordModal.password}
                </code>
                <p className="text-[10px] text-slate-400 text-center mt-1.5">
                  Click the code to select all · Use the Copy button to copy exactly
                </p>
              </div>
              <p className="text-xs text-slate-600">{passwordModal.message}</p>
              <div className="bg-teal-50 border border-teal-200 rounded p-3 text-xs text-teal-800">
                📋 <strong>Tell the user:</strong> When they log in with this password, they'll be asked to set their own permanent password.
                It must be <strong>≥ 12 characters</strong> with uppercase, lowercase, a number, and a special character (e.g. !@#$).
              </div>
            </div>
            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
              <button onClick={() => setPasswordModal(null)} className="px-4 py-2 bg-[#1F7A6E] text-white hover:bg-[#14504E] rounded text-sm" data-testid="close-password-modal">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      <BulkEmployeeImportModal
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onSuccess={() => loadData()}
      />
    </div>
  );
}
